<template>
  <div class="col-12 col-xl-12" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card mb-3 " style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3">{{ $t('classes.table.title') }}</h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading"
            :message="$t('classes.table.loading')"
            class="py-3 py-5"
          />

          <template v-else>
            <div class="d-block d-xl-none">
              <div
                v-if="filteredClasses.length === 0"
                class="text-center py-3 text-muted"
              >
                <template v-if="searchQuery">
                  <i class="fas fa-search fa-2x mb-2"></i>
                  <p>{{ $t('classes.table.noResults') }}</p>
                </template>
                <template v-else>
                  <i class="fas fa-folder-open fa-2x mb-2"></i>
                  <p>{{ $t('classes.table.noClasses') }}</p>
                </template>
              </div>

              <div
                v-for="classItem in filteredClasses"
                :key="'mobile-' + classItem.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-sm">{{ classItem.name }}</h6>
                    </div>
                    <div class="d-flex">
                      <button
                        @click="navigateToGroups(classItem.id)"
                        class="btn btn-link text-primary p-0 mx-1"
                        :title="$t('classes.actions.groups')"
                          v-can="'class_groups.display'"

                      >
                        <i class="fas fa-users fa-xs"></i>
                      </button>
                      <button
                        @click="navigateToSubjects(classItem.id)"
                        class="btn btn-link text-info p-0 mx-1"
                        :title="$t('classes.actions.subjects')"
                          v-can="'class_subjects.display'"

                      >
                        <i class="fas fa-book fa-xs"></i>
                      </button>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <button
                      @click="navigateToEditClass(classItem.id)"
                      class="btn btn-link text-secondary p-0 mx-1"
                      :title="$t('classes.actions.edit')"
                      v-can="'classes.update'"
                    >
                      <i class="fas fa-edit fa-xs"></i>
                    </button>
                    <button
                      @click="$emit('delete', classItem.id)"
                      class="btn btn-link text-danger p-0 mx-1"
                      :title="$t('classes.actions.delete')"
                      v-can="'classes.delete'"
                    >
                      <i class="fas fa-trash fa-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('classes.table.name') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('classes.table.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredClasses.length === 0">
                  <td colspan="2" class="text-center py-4 text-muted">
                    <template v-if="searchQuery">
                      <i class="fas fa-search fa-2x mb-2"></i>
                      <p>{{ $t('classes.table.noResults') }} "{{ searchQuery }}"</p>
                    </template>
                    <template v-else>
                      <i class="fas fa-folder-open fa-2x mb-2"></i>
                      <p>{{ $t('classes.table.noClasses') }}</p>
                    </template>
                  </td>
                </tr>

                <tr v-for="classItem in filteredClasses" :key="classItem.id">
                  <td>
                    <div class="d-flex px-3 py-1">
                      <div class="d-flex flex-column justify-content-center">
                        <h6 class="mb-0 text-md">{{ classItem.name }}</h6>
                      </div>
                    </div>
                  </td>
                  <td class="align-middle text-center pe-4">
                    <div class="d-inline-flex">
                      <button
                        @click="navigateToGroups(classItem.id)"
                        class="btn btn-link text-primary p-1 mx-1"
                        :title="$t('classes.actions.groups')"
                        v-can="'class_groups.display'"
                      >
                        <i class="fas fa-users fa-sm"></i>
                      </button>
                      <button
                        @click="navigateToSubjects(classItem.id)"
                        class="btn btn-link text-info p-1 mx-1"
                        :title="$t('classes.actions.subjects')"
                        v-can="'class_subjects.display'"
                      >
                        <i class="fas fa-book fa-sm"></i>
                      </button>
                      <button
                        @click="navigateToEditClass(classItem.id)"
                        class="btn btn-link text-secondary p-1 mx-1"
                        :title="$t('classes.actions.edit')"
                        v-can="'classes.update'"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </button>
                      <button
                        @click="$emit('delete', classItem.id)"
                        class="btn btn-link text-danger p-1 mx-1"
                        :title="$t('classes.actions.delete')"
                        v-can="'classes.delete'"
                      >
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { locale } = useI18n();
const router = useRouter();
const searchQuery = ref("");

const props = defineProps({
  classes: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});


const filteredClasses = computed(() => {
  if (!searchQuery.value) return props.classes;
  return props.classes.filter((classItem) =>
    classItem.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const navigateToGroups = (classId) => {
  router.push(`/classes/${classId}/groups`);
};

const navigateToSubjects = (classId) => {
  router.push(`/classes/${classId}/subjects`);
};

const navigateToEditClass = (classId) => {
  router.push(`/classes/${classId}/edit`);
};
</script>